'use client'

/**
 * The acting-identity block at the foot of the rail, and the switch it opens.
 *
 * There is no equivalent in the base asset: it has users, not authorities, and nothing
 * that distinguishes the identity signed in from the authority an action is recorded
 * under. So this is assembled from parts that already exist rather than authored as a new
 * visual language — `UserMenu`'s `DropdownMenu` pattern from `Header.tsx`, the federal
 * tree's `ReasonField` for the written reason, and `StateBadge`'s pill for the gate chips.
 *
 * Three rules it exists to enforce, none of them cosmetic:
 *
 *  1. Only an unrestricted authority may act under another. Where the holder cannot, the
 *     block says so in the server's own words instead of hiding the control. A presenter
 *     who cannot switch needs to know why, not to find a missing button.
 *  2. A switch takes a written reason. The confirm control stays disabled until one is
 *     typed, so the endpoint's own 422 is a guard rather than a path anyone walks.
 *  3. Every option carries its authority title as text. Gate reach, override and
 *     funds-obligation are words and glyphs, never colour alone.
 *
 * Until the persona seed lands the block renders nothing at all: `GET /api/scc/identity`
 * answers 404 on a build without it, and `resourceFromError` maps that to `unavailable`,
 * which is a fact about the build rather than an error to retry. Naming that absence in the
 * rail read as a fault to everyone who saw it, so the whole block now stands down until the
 * endpoint answers. It is still deliberately not given placeholder personas to look
 * populated with, and the rail's divider goes with it via `empty:hidden` in `Sidebar`.
 */

import * as React from 'react'
import { useSession } from 'next-auth/react'

import { Icons } from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ReasonField, isReasonSufficient } from '@/components/scc/interactions/ReasonField'
import { cn } from '@/lib/utils'
import { federalPost, useFederalResource } from '@/lib/scc-api'
import {
  useActingIdentity,
  type IdentityPayload,
  type PersonaOption,
} from '@/context/ActingIdentityContext'

const IDENTITY_PATH = '/api/scc/identity'
const ASSUME_PATH = '/api/scc/identity/assume'

/**
 * The endpoint rejects a reason shorter than this, so the control is disabled below it
 * too. A one-word reason in a protest file is not a reason.
 */
const MIN_REASON_LENGTH = 12

export interface PersonaSwitcherProps {
  /** In the collapsed rail the block is a single glyph with a tooltip. */
  isCollapsed?: boolean
}

/** A gate reach, an override right or a funds obligation, as a word. */
function OptionChip({ children }: { children: React.ReactNode }) {
  return (
    <span className='rounded-full bg-black/[0.04] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-brand-muted'>
      {children}
    </span>
  )
}

/**
 * `FG1`–`FG7` collapses to `FG1–FG7` where the reach is contiguous, because seven chips
 * on one row wrap and stop being readable.
 */
function gateSummary(gates: string[]): string | null {
  if (!gates || gates.length === 0) return null
  if (gates.length === 1) return gates[0]
  const numbers = gates
    .map((gate) => Number(gate.replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)
  const contiguous =
    numbers.length === gates.length &&
    numbers.every((n, i) => i === 0 || n === numbers[i - 1] + 1)
  if (contiguous) return `FG${numbers[0]}–FG${numbers[numbers.length - 1]}`
  return gates.join(' ')
}

function PersonaRow({
  option,
  selected,
  onSelect,
}: {
  option: PersonaOption
  selected: boolean
  onSelect: () => void
}) {
  const gates = gateSummary(option.gates)
  return (
    <button
      type='button'
      role='menuitemradio'
      aria-checked={selected}
      disabled={option.is_current}
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-2 rounded-lg px-2 py-2 text-left',
        'transition-colors duration-150',
        option.is_current
          ? 'cursor-default bg-brand-cornflower/10'
          : 'hover:bg-black/[0.04]',
        selected && !option.is_current && 'bg-brand-navy/[0.06]'
      )}
    >
      <span className='mt-0.5 shrink-0'>
        {option.can_override ? (
          <Icons.shield className='h-3.5 w-3.5 text-brand-navy' strokeWidth={1.5} />
        ) : option.obligates_funds ? (
          <Icons.zap className='h-3.5 w-3.5 text-brand-navy' strokeWidth={1.5} />
        ) : (
          <Icons.user className='h-3.5 w-3.5 text-brand-muted' strokeWidth={1.5} />
        )}
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block truncate text-sm text-foreground'>
          {option.authority_title}
        </span>
        <span className='mt-1 flex flex-wrap items-center gap-1'>
          {gates && <OptionChip>{gates}</OptionChip>}
          {option.obligates_funds && <OptionChip>obligates funds</OptionChip>}
          {option.can_override && <OptionChip>override</OptionChip>}
          {option.binds_government && <OptionChip>binds government</OptionChip>}
          {typeof option.open_approval_count === 'number' &&
            option.open_approval_count > 0 && (
              <OptionChip>{option.open_approval_count} open</OptionChip>
            )}
          {option.seeded_identity?.is_illustrative && (
            <OptionChip>illustrative</OptionChip>
          )}
        </span>
      </span>
      {option.is_current && (
        <span className='shrink-0 text-[10px] font-semibold uppercase tracking-widest text-brand-navy'>
          now
        </span>
      )}
    </button>
  )
}

export function PersonaSwitcher({ isCollapsed }: PersonaSwitcherProps) {
  const { status: sessionStatus } = useSession()
  const { setAssumedAuthority } = useActingIdentity()
  const [open, setOpen] = React.useState(false)
  const [selected, setSelected] = React.useState<PersonaOption | null>(null)
  const [reason, setReason] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)
  const [failure, setFailure] = React.useState<string | null>(null)

  const { resource, reload } = useFederalResource<IdentityPayload>(IDENTITY_PATH, {
    skip: sessionStatus !== 'authenticated',
  })

  // The signed-out rail already drops its user block; the acting block goes with it.
  if (sessionStatus === 'unauthenticated') return null

  const close = () => {
    setOpen(false)
    setSelected(null)
    setReason('')
    setFailure(null)
  }

  const confirm = async () => {
    if (!selected) return
    setSubmitting(true)
    setFailure(null)
    try {
      await federalPost(ASSUME_PATH, {
        authority_code: selected.authority_code,
        reason,
      })
      setAssumedAuthority(selected.authority_code)
      toast.decision(`Now acting as ${selected.authority_title}`, {
        authority: selected.authority_title,
        reason,
      })
      close()
      reload()
    } catch (error) {
      // The server's own sentence, kept verbatim: on this path it names the roles the
      // token presented, which is the one message that lets a presenter fix their login.
      setFailure(error instanceof Error ? error.message : String(error))
    } finally {
      setSubmitting(false)
    }
  }

  const block = (children: React.ReactNode) => (
    <div
      aria-live='polite'
      className={cn(
        'rounded-xl border border-black/[0.04] bg-black/[0.02] p-3',
        isCollapsed && 'flex items-center justify-center p-2'
      )}
    >
      {children}
    </div>
  )

  // Never collapse the block's height between states: the rail must not jump when the
  // read lands.
  if (resource.status === 'loading') {
    return block(
      isCollapsed ? (
        <Skeleton variant='circular' className='h-5 w-5' />
      ) : (
        <div className='space-y-2'>
          <Skeleton variant='text' className='h-3 w-24' />
          <Skeleton variant='text' className='h-3 w-32' />
        </div>
      )
    )
  }

  // No identity to report, so no block. There is nothing here a presenter can act on:
  // the switch needs the very payload that is missing, so naming the gap only spends rail
  // on a dead end. Restoring the message is a matter of rendering `resource.error` here
  // again once `/api/scc/identity` is built.
  if (resource.status !== 'ready' || !resource.data) return null

  const { acting, holder, assumable } = resource.data
  const actingTitle = acting.authority_title ?? holder.authority_title

  if (isCollapsed) {
    return block(
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span>
            <Icons.shield className='h-5 w-5 text-brand-navy' strokeWidth={1.5} />
          </span>
        </TooltipTrigger>
        <TooltipContent side='right' className='ml-2'>
          Acting as {actingTitle}
        </TooltipContent>
      </Tooltip>
    )
  }

  // The holder cannot exchange its authority. State the rule rather than hide the
  // control — this is `deps.py`'s refusal, surfaced as copy before anyone triggers it.
  if (!holder.may_assume) {
    return block(
      <>
        <p className='text-[10px] font-semibold uppercase tracking-widest text-brand-muted/70'>
          Acting as
        </p>
        <p className='mt-0.5 truncate text-sm font-medium text-brand-navy'>
          {actingTitle}
        </p>
        <p className='mt-1 text-xs leading-relaxed text-brand-muted'>
          Your authority cannot be exchanged. Only an unrestricted authority may act
          under another, and every such action records both.
        </p>
      </>
    )
  }

  const ready = isReasonSufficient(reason) && reason.trim().length >= MIN_REASON_LENGTH

  return block(
    <DropdownMenu
      open={open}
      onOpenChange={(next) => (next ? setOpen(true) : close())}
    >
      <DropdownMenuTrigger asChild>
        <button
          type='button'
          aria-haspopup='menu'
          className='w-full text-left focus-ring rounded-lg'
        >
          <span className='block text-[10px] font-semibold uppercase tracking-widest text-brand-muted/70'>
            Acting as
          </span>
          <span className='mt-0.5 flex items-center gap-1.5'>
            <Icons.shield
              className='h-3.5 w-3.5 shrink-0 text-brand-navy'
              strokeWidth={1.5}
            />
            <span className='truncate text-sm font-medium text-brand-navy'>
              {actingTitle}
            </span>
          </span>
          <span className='mt-0.5 flex items-center gap-1 text-xs text-brand-muted'>
            Switch persona
            <Icons.chevronDown className='h-3 w-3' strokeWidth={1.5} />
          </span>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side='top'
        align='start'
        className='w-80 p-0'
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <div className='border-b border-border/60 px-3 py-2'>
          <p className='text-[10px] font-semibold uppercase tracking-widest text-brand-muted/70'>
            Switch acting identity
          </p>
          <p className='mt-1 text-xs text-brand-muted'>
            You hold: {holder.authority_title}
            {holder.is_unrestricted ? ' (unrestricted)' : ''}. Every action records both
            identities.
          </p>
        </div>

        <div className='max-h-64 overflow-y-auto p-1' role='menu'>
          {assumable.length === 0 ? (
            <p className='px-2 py-6 text-center text-xs text-brand-muted'>
              No other authority is available to act under on this build.
            </p>
          ) : (
            assumable.map((option) => (
              <PersonaRow
                key={option.authority_code}
                option={option}
                selected={selected?.authority_code === option.authority_code}
                onSelect={() => setSelected(option)}
              />
            ))
          )}
        </div>

        {selected && (
          <div className='space-y-3 border-t border-border/60 p-3'>
            <ReasonField
              id='persona-switch-reason'
              label='Reason for switch (required)'
              help={`Recorded on the audit entry for acting as ${selected.authority_title}.`}
              value={reason}
              onChange={setReason}
              disabled={submitting}
              rows={2}
            />
            {failure && (
              <p className='text-xs text-red-600'>{failure}</p>
            )}
            <div className='flex justify-end gap-2'>
              <Button variant='ghost' size='sm' onClick={close} disabled={submitting}>
                Cancel
              </Button>
              <Button size='sm' onClick={confirm} disabled={!ready || submitting}>
                Act as {selected.authority_title}
              </Button>
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
